import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, TextField, MenuItem, Stack, 
  IconButton, Tooltip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, CircularProgress, Skeleton, Snackbar,
} from '@mui/material';
import {
  Send as SendIcon, CheckCircle as CheckCircleIcon, TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon, FitnessCenter as TrainerIcon, Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAppData } from '../context/AppDataContext';
import { retentionAPI } from '../services/api';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';

// ── Utility function to extract ID string from various object formats ──────────
function extractIdString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value._id) return String(value._id);
    if (value.$oid) return value.$oid;
    if (value.id) return String(value.id);
  }
  return String(value);
}

// ── Stat card component ────────────────────────────────────────────────────────
function StatCard({ title, value, color, icon, caption }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        height: '100%',
        border: `1px solid ${color}20`,
        backgroundColor: `${color}08`,
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${color}15`,
        }
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, bottom: 0, bgcolor: color }} />
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            width: 48, height: 48, borderRadius: 2.5,
            bgcolor: `${color}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 24, color } })}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '1.875rem', fontWeight: 900, color: 'text.primary', lineHeight: 1.1, mb: 0.5 }}>
            {value}
          </Typography>
          {caption && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {caption}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

// ── Skeleton row ──────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <TableRow>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
      <TableCell><Skeleton /></TableCell>
    </TableRow>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <TrendingDownIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
      <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
        No alerts found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        All members are active and engaged
      </Typography>
    </Box>
  );
}

// ── Risk color map ────────────────────────────────────────────────────────────
const riskColorMap = {
  high: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', glow: 'rgba(220, 38, 38, 0.2)' },
  medium: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A', glow: 'rgba(217, 119, 6, 0.2)' },
  low: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE', glow: 'rgba(29, 78, 216, 0.2)' },
};

function formatRiskReason(reason) {
  const expiryMatch = String(reason || '').match(/^Membership expires in (-?\d+) days$/i);
  if (!expiryMatch) return reason;

  const days = Number(expiryMatch[1]);
  if (days < 0) return 'Membership expired';
  if (days === 0) return 'Membership expires today';
  return reason;
}

export default function Retention() {
  const { gyms, reloadGyms } = useAppData();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [summary, setSummary] = useState({
    atRiskMembers: 0,
    needsTrainerFollowUp: 0,
    nudgedMembers: 0,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
  });
  
  const [filters, setFilters] = useState({
    riskLevel: '',
    isResolved: '',
    gymId: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [nudgeDialog, setNudgeDialog] = useState({ open: false, alertId: null, memberName: '' });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const defaultGymId = user.gymId
    ? extractIdString(user.gymId)
    : (gyms.length > 0 ? extractIdString(gyms[0]._id) : '');

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, limit]);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.gymId]);

  useEffect(() => {
    if (user.role === 'superadmin' && gyms.length === 0) {
      reloadGyms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role, gyms.length]);

  const loadSummary = async () => {
    try {
      const params = {};
      if (filters.gymId) params.gymId = filters.gymId;
      const response = await retentionAPI.summary(params);
      setSummary(response.data.data || {});
    } catch (error) {
      console.error('Error loading retention summary:', error);
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.riskLevel) params.riskLevel = filters.riskLevel;
      if (filters.isResolved !== '') params.isResolved = filters.isResolved;
      if (filters.gymId) params.gymId = filters.gymId;

      const response = await retentionAPI.getAlerts(params);
      setAlerts(response.data.data || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading alerts:', error);
      setSnackbar({ open: true, message: 'Failed to load alerts', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    const scanGymId = extractIdString(filters.gymId || defaultGymId);
    if (!scanGymId) {
      setSnackbar({ open: true, message: 'Please select a gym', type: 'error' });
      return;
    }

    setScanning(true);
    try {
      const response = await retentionAPI.scan(scanGymId);
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message || 'Scan completed',
          type: 'success',
        });
        loadAlerts();
        loadSummary();
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Scan failed', type: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to trigger scan', type: 'error' });
    } finally {
      setScanning(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const normalizedValue = key === 'gymId' ? extractIdString(value) : value;
    setFilters(prev => ({ ...prev, [key]: normalizedValue }));
    setPage(1);
  };

  const handleResolve = async (alertId) => {
    try {
      await retentionAPI.resolve(alertId);
      setSnackbar({ open: true, message: 'Alert resolved', type: 'success' });
      loadAlerts();
      loadSummary();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to resolve alert', type: 'error' });
    }
  };

  const handleNudge = (alertId, memberName) => {
    setNudgeDialog({ open: true, alertId, memberName });
  };

  const sendNudge = async () => {
    try {
      await retentionAPI.nudge(nudgeDialog.alertId);
      setSnackbar({ open: true, message: 'Nudge sent to member', type: 'success' });
      setNudgeDialog({ open: false, alertId: null, memberName: '' });
      loadAlerts();
      loadSummary();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to send nudge', type: 'error' });
    }
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Member Retention"
        subtitle="Monitor at-risk members and send engagement nudges"
        gradient="linear-gradient(90deg, #DC2626, #F87171)"
      />

      {/* Filter & Scan */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800} mb={3}>Filter & Scan</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
          {user.role === 'superadmin' && (
            <TextField
              select
              label="Gym"
              size="small"
              sx={{ minWidth: 200 }}
              value={filters.gymId}
              onChange={(e) => handleFilterChange('gymId', e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return 'All Gyms';
                  const g = gyms.find(gym => gym._id === selected);
                  return g ? g.name : 'All Gyms';
                }
              }}
            >
              <MenuItem value="">All Gyms</MenuItem>
              {gyms.map(gym => (
                <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="Risk Level"
            size="small"
            sx={{ minWidth: 150 }}
            value={filters.riskLevel}
            onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) return 'All Levels';
                if (selected === 'high') return 'High';
                if (selected === 'medium') return 'Medium';
                if (selected === 'low') return 'Low';
                return selected;
              }
            }}
          >
            <MenuItem value="">All Levels</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </TextField>

          <TextField
            select
            label="Status"
            size="small"
            sx={{ minWidth: 150 }}
            value={filters.isResolved}
            onChange={(e) => handleFilterChange('isResolved', e.target.value)}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (selected === '') return 'All';
                if (selected === 'false') return 'Active';
                if (selected === 'true') return 'Resolved';
                return selected;
              }
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="false">Active</MenuItem>
            <MenuItem value="true">Resolved</MenuItem>
          </TextField>

          <Button
            variant="contained"
            startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            onClick={handleScan}
            disabled={scanning}
            sx={{ minWidth: 140, height: 40, fontWeight: 700, borderRadius: 2 }}
          >
            {scanning ? 'Scanning...' : 'Scan Now'}
          </Button>
        </Stack>
      </Paper>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="At-risk Members"
            value={summary.atRiskMembers || 0}
            color="#DC2626"
            icon={<TrendingDownIcon />}
            caption={`${summary.mediumRisk || 0} medium / ${summary.lowRisk || 0} low`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Trainer Follow-up"
            value={summary.needsTrainerFollowUp || 0}
            color="#7C3AED"
            icon={<TrainerIcon />}
            caption="Assigned members needing human touch"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="High Risk"
            value={summary.highRisk || 0}
            color="#D97706"
            icon={<TrendingDownIcon />}
            caption="Priority comeback list"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nudges Sent"
            value={summary.nudgedMembers || 0}
            color="#059669"
            icon={<NotificationsIcon />}
            caption="Members already contacted"
          />
        </Grid>
      </Grid>

      {/* Chart & Filter Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={800} mb={2}>Risk Distribution</Typography>
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'High Risk', value: summary.highRisk || 0, color: '#DC2626' },
                      { name: 'Medium Risk', value: summary.mediumRisk || 0, color: '#D97706' },
                      { name: 'Low Risk', value: summary.lowRisk || 0, color: '#1D4ED8' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {([
                      { name: 'High Risk', value: summary.highRisk || 0, color: '#DC2626' },
                      { name: 'Medium Risk', value: summary.mediumRisk || 0, color: '#D97706' },
                      { name: 'Low Risk', value: summary.lowRisk || 0, color: '#1D4ED8' }
                    ]).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '0.875rem', fontWeight: 600 }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.paper' }}>
              <TableCell>Member</TableCell>
              <TableCell>Last Check-in</TableCell>
              <TableCell align="center">Days Inactive</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell align="center">Risk</TableCell>
              <TableCell>Reasons</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
            
            {!loading && alerts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState />
                </TableCell>
              </TableRow>
            )}

            {!loading && alerts.map((alert) => (
              <TableRow key={alert._id} sx={{ '&:hover': { backgroundColor: 'background.paper' } }}>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'action.hover', color: 'text.secondary' }}>
                      {alert.userId?.name?.[0]?.toUpperCase() || 'M'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {alert.userId?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {alert.userId?.email || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {alert.lastCheckInDate
                      ? new Date(alert.lastCheckInDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1, height: 6, bgcolor: 'action.hover', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min((alert.daysSinceLastCheckin / 30) * 100, 100)}%`, height: '100%', bgcolor: alert.daysSinceLastCheckin > 14 ? '#DC2626' : (alert.daysSinceLastCheckin > 7 ? '#D97706' : '#059669'), borderRadius: 3 }} />
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 28, textAlign: 'right' }}>
                        {alert.daysSinceLastCheckin}d
                      </Typography>
                    </Box>
                    {alert.attendanceDropPercentage >= 40 && (
                      <Chip
                        label={`📉 ${alert.attendanceDropPercentage}% drop`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {alert.membershipExpiryDate
                      ? new Date(alert.membershipExpiryDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '—'}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={alert.riskLevel.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: riskColorMap[alert.riskLevel].bg,
                      color: riskColorMap[alert.riskLevel].color,
                      border: `1px solid ${riskColorMap[alert.riskLevel].border}`,
                      fontWeight: 800,
                      boxShadow: `0 0 10px ${riskColorMap[alert.riskLevel].glow}`,
                      letterSpacing: '0.05em'
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {alert.attendanceDropPercentage >= 40 && (
                      <Chip
                        label={`${alert.baselineVisitsCount} → ${alert.recentVisitsCount} visits/mo`}
                        size="small"
                        color="warning"
                        sx={{ fontSize: '0.7rem', height: 24, fontWeight: 700 }}
                      />
                    )}
                    {alert.riskReasons.slice(0, 2).map((reason, i) => (
                      <Chip
                        key={i}
                        label={formatRiskReason(reason)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    ))}
                    {alert.riskReasons.length > 2 && (
                      <Tooltip title={alert.riskReasons.slice(2).map(formatRiskReason).join(', ')}>
                        <Chip label={`+${alert.riskReasons.length - 2}`} size="small" variant="outlined" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>

                <TableCell align="center">
                  <Stack direction="row" justifyContent="center" spacing={1}>
                    {!alert.isResolved && (
                      <>
                        <Tooltip title="Send nudge notification">
                          <IconButton
                            size="small"
                            onClick={() => handleNudge(alert._id, alert.userId?.name)}
                            sx={{ color: '#059669', bgcolor: '#ECFDF5', '&:hover': { bgcolor: '#D1FAE5' } }}
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark resolved">
                          <IconButton
                            size="small"
                            onClick={() => handleResolve(alert._id)}
                            sx={{ color: 'text.secondary', bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {alert.isResolved && (
                      <Chip label="Resolved" size="small" sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 600, border: '1px solid', borderColor: 'divider' }} />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {!loading && pages > 1 && (
        <PaginationBar
          page={page}
          pages={pages}
          total={total}
          limit={limit}
          onPage={setPage}
          onLimit={setLimit}
        />
      )}

      {/* Nudge Dialog */}
      <Dialog open={nudgeDialog.open} onClose={() => setNudgeDialog({ open: false, alertId: null, memberName: '' })}>
        <DialogTitle>Send Nudge</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Send an engagement nudge to <strong>{nudgeDialog.memberName}</strong> to encourage them to return?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNudgeDialog({ open: false, alertId: null, memberName: '' })}>
            Cancel
          </Button>
          <Button onClick={sendNudge} variant="contained" color="primary">
            Send Nudge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
}
